import { DialogComponent } from '@theme/dialog';

/**
 * Product Story player (Instagram-style). Extends DialogComponent to inherit
 * showDialog()/closeDialog(), focus-trap, scroll-lock, Escape + backdrop close
 * and the dialog:open / dialog:close events. Playback (Swiper, autoplay,
 * progress bars, sound, play/pause) is wired to those two events.
 *
 * Progress bars: the CSS custom properties `--story-transition-duration` and
 * `--story-dots-active-width` are set on the HOST element (`this`) — not on the
 * swiper container — because the pagination bullets that consume them are
 * descendants of `<story-player>` but siblings of `<swiper-container>`, and
 * custom properties only cascade to descendants.
 *
 * Timing is tracked in elapsed milliseconds per slide so pause/resume freezes
 * and continues the bar in place (custom properties don't hold interpolated
 * transition values, so a getComputedStyle readback can't freeze them).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement & { swiper: any; initialize: () => void }} swiper
 * @property {HTMLElement} pagination
 * @property {HTMLElement} [options]
 * @property {HTMLButtonElement} [soundBtn]
 * @property {HTMLButtonElement} [playBtn]
 * @property {HTMLButtonElement} [pauseBtn]
 *
 * @extends {DialogComponent}
 */
class StoryPlayer extends DialogComponent {
  requiredRefs = ['dialog', 'swiper', 'pagination'];

  /** @type {any} */
  #swiper = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timeout = null;
  /** @type {HTMLVideoElement | null} */
  #video = null;
  /** @type {AbortController | null} Per-slide controller for video listeners. */
  #videoAbort = null;
  #playing = false;
  #imageDuration = 5000;
  /** @type {number} ms elapsed on the current slide before the running segment. */
  #slideElapsed = 0;
  /** @type {number} performance.now() when the current image segment started. */
  #segmentStart = 0;
  /** @type {string|null} Real index of the loaded slide (dedupes re-fires). */
  #currentKey = null;
  #segmentActive = false;
  /** @type {AbortController | null} Controller for host-level listeners. */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();
    this.#imageDuration = Number(this.dataset.imageDuration) || 5000;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('dialog:open', this.#onOpen, { signal });
    this.addEventListener('dialog:close', this.#onClose, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimer();
    this.#videoAbort?.abort();
    this.#videoAbort = null;
    this.#controller?.abort();
    this.#controller = null;
    try {
      this.#swiper?.destroy(true, true);
    } catch (_) {
      // Swiper can throw during teardown if already destroyed.
    }
    this.#swiper = null;
  }

  #onOpen = async () => {
    await customElements.whenDefined('swiper-container');
    if (!this.#swiper) this.#initSwiper();
    this.#swiper?.update();

    // Reset state BEFORE slideToLoop so a synchronous slideChange re-fire is
    // deduped by #currentKey rather than double-starting the segment.
    this.#playing = true;
    this.#setPlayingUI(true);
    this.#currentKey = null;
    this.#segmentActive = false;
    this.#slideElapsed = 0;

    // loop:true prepends clones, so physical index 0 is a clone of the last
    // real slide — slideToLoop() maps the logical index to the correct slide.
    this.#swiper?.slideToLoop(0, 0);
    this.#loadSlide();
  };

  #onClose = () => {
    this.#playing = false;
    this.#clearTimer();
    this.#videoAbort?.abort();
    this.#videoAbort = null;
    for (const video of this.querySelectorAll('video')) {
      video.pause();
      video.currentTime = 0;
      video.muted = true;
    }
    this.#video = null;
    this.#segmentActive = false;
    this.#currentKey = null;
    this.#slideElapsed = 0;
    // Reset the options overlay so it never reappears on the next open.
    this.closeOptions();
  };

  #initSwiper() {
    const swiperEl = this.refs.swiper;
    if (!swiperEl || swiperEl.swiper) return;

    Object.assign(swiperEl, {
      slidesPerView: 1,
      loop: true,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      speed: 120,
      allowTouchMove: false,
      pagination: {
        el: this.refs.pagination,
        clickable: true,
      },
      on: {
        // Stop the outgoing segment the instant a transition starts (covers
        // arrows, taps, pagination clicks and auto-advance) so a stale
        // timer/`ended` can't fire mid-transition and double-advance.
        slideChangeTransitionStart: () => this.#stopSegment(),
        slideChangeTransitionEnd: () => this.#loadSlide(),
      },
    });

    swiperEl.initialize();
    swiperEl.removeAttribute('init');
    this.#swiper = swiperEl.swiper;

    // Load all CFH-lazy slides now (off-viewport in an overflow:hidden carousel).
    document.dispatchEvent(
      new CustomEvent('removeLazyKeep', { detail: { lazyParentElement: swiperEl } })
    );
  }

  #currentSlide() {
    return (
      this.refs.swiper?.querySelector('.swiper-slide-active') ??
      this.#swiper?.slides?.[this.#swiper?.activeIndex ?? 0] ??
      this.querySelector('swiper-slide')
    );
  }

  /**
   * Prepare a freshly-shown slide: tear down the previous video, reset elapsed
   * time, wire the current slide, then run it (or freeze it if paused).
   * Deduped by #currentKey so a slideChange re-fire for the same slide is a no-op.
   */
  #loadSlide() {
    const slide = this.#currentSlide();
    if (!slide) return;

    const key = slide.getAttribute('data-swiper-slide-index') ?? String(this.#swiper?.realIndex ?? 0);
    if (key === this.#currentKey && this.#segmentActive) return;
    this.#currentKey = key;

    // Tear down the previous segment.
    this.#clearTimer();
    this.#videoAbort?.abort();
    this.#videoAbort = null;
    if (this.#video) {
      this.#video.pause();
      this.#video.currentTime = 0;
      this.#video = null;
    }
    this.#slideElapsed = 0;
    this.#segmentStart = 0;

    const type = slide.getAttribute('data-slide-type');
    this.#showSoundButton(type === 'video');

    if (type === 'video') {
      const video = /** @type {HTMLVideoElement | null} */ (slide.querySelector('video'));
      if (video && document.body.contains(video)) {
        this.#video = video;
        video.currentTime = 0;
        this.#syncSoundIcon();
        this.#videoAbort = new AbortController();
        video.addEventListener('ended', () => this.next(), { signal: this.#videoAbort.signal });
      }
    }

    this.#segmentActive = true;
    if (this.#playing) this.#run();
    else this.#freezeBar(0);
  }

  /** Total ms the current slide's progress bar should span. */
  #slideTotal(slide, type) {
    if (type === 'video' && this.#video && isFinite(this.#video.duration) && this.#video.duration > 0) {
      return this.#video.duration * 1000;
    }
    if (type === 'video') {
      return Number(slide?.getAttribute('data-video-duration')) || this.#imageDuration;
    }
    return this.#imageDuration;
  }

  /** Start or resume the current slide's timer + bar from #slideElapsed. */
  #run() {
    const slide = this.#currentSlide();
    if (!slide) return;
    const type = slide.getAttribute('data-slide-type');
    const total = this.#slideTotal(slide, type);
    const remaining = Math.max(0, total - this.#slideElapsed);
    const fromPct = total > 0 ? (this.#slideElapsed / total) * 100 : 0;

    this.#animateBar(fromPct, remaining);

    if (type === 'video' && this.#video) {
      this.#video.currentTime = this.#slideElapsed / 1000;
      this.#video.play().catch(() => {});
      // Advance is normally driven by the 'ended' listener (wired in
      // #loadSlide); this timer is a safety net so a failed/stalled video
      // still advances. Whichever fires first advances; #stopSegment cancels
      // the other at slideChangeTransitionStart.
      this.#timeout = setTimeout(() => this.next(), remaining);
      return;
    }

    this.#segmentStart = performance.now();
    this.#timeout = setTimeout(() => this.next(), remaining);
  }

  /** Cancel the running segment's timer + video listeners (no state reset). */
  #stopSegment() {
    this.#clearTimer();
    this.#videoAbort?.abort();
    this.#videoAbort = null;
    if (this.#video) {
      this.#video.pause();
      this.#video = null;
    }
  }

  /** Animate the active bar from `fromPct`% to 100% over `durationMs`. */
  #animateBar(fromPct, durationMs) {
    this.style.setProperty('--story-transition-duration', '0ms');
    this.style.setProperty('--story-dots-active-width', `${Math.max(0, Math.min(100, fromPct))}%`);
    // Force a reflow so the instant jump commits before the animated set.
    void this.offsetWidth;
    this.style.setProperty('--story-transition-duration', `${Math.max(0, durationMs)}ms`);
    this.style.setProperty('--story-dots-active-width', '100%');
  }

  /** Pin the active bar at `pct`% with no transition (freeze). */
  #freezeBar(pct) {
    this.style.setProperty('--story-transition-duration', '0ms');
    this.style.setProperty('--story-dots-active-width', `${Math.max(0, Math.min(100, pct))}%`);
  }

  #clearTimer() {
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
  }

  #showSoundButton(show) {
    if (this.refs.soundBtn) this.refs.soundBtn.hidden = !show;
  }

  #syncSoundIcon() {
    const btn = this.refs.soundBtn;
    if (!btn) return;
    const muted = this.#video ? this.#video.muted : true;
    const on = btn.querySelector('.story-player__sound-on');
    const off = btn.querySelector('.story-player__sound-off');
    if (on) on.hidden = muted;
    if (off) off.hidden = !muted;
    btn.setAttribute('aria-label', muted ? btn.dataset.labelUnmute ?? 'Unmute' : btn.dataset.labelMute ?? 'Mute');
  }

  #setPlayingUI(playing) {
    if (this.refs.playBtn) this.refs.playBtn.hidden = playing;
    if (this.refs.pauseBtn) this.refs.pauseBtn.hidden = !playing;
  }

  #onKeyDown = (event) => {
    if (event.key === 'ArrowRight') this.next();
    else if (event.key === 'ArrowLeft') this.prev();
  };

  // --- Public methods (via on: bindings) ---

  prev() {
    this.#swiper?.slidePrev();
  }

  next() {
    this.#swiper?.slideNext();
  }

  play() {
    if (this.#playing) return;
    this.#playing = true;
    this.#setPlayingUI(true);
    if (this.#segmentActive) this.#run();
    else this.#loadSlide();
  }

  pause() {
    if (!this.#playing) return;
    this.#playing = false;
    this.#setPlayingUI(false);
    this.#clearTimer();

    const slide = this.#currentSlide();
    const type = slide?.getAttribute('data-slide-type');
    const total = this.#slideTotal(slide, type);

    if (type === 'video' && this.#video) {
      this.#video.pause();
      this.#slideElapsed = this.#video.currentTime * 1000;
    } else if (this.#segmentStart) {
      this.#slideElapsed += performance.now() - this.#segmentStart;
      this.#segmentStart = 0;
    }

    const pct = total > 0 ? (this.#slideElapsed / total) * 100 : 0;
    this.#freezeBar(pct);
  }

  toggleSound(event) {
    event?.stopPropagation();
    if (!this.#video) return;
    this.#video.muted = !this.#video.muted;
    this.#syncSoundIcon();
  }

  toggleOptions() {
    this.refs.options?.classList.toggle('is-open');
  }

  closeOptions() {
    this.refs.options?.classList.remove('is-open');
  }

  /**
   * "Find out more": close the story (which releases the scroll-lock and the
   * modal top layer), then scroll to the product focus section. Fragment
   * navigation can't do this itself while the dialog is still modal.
   *
   * @param {Event} [event]
   */
  async findOutMore(event) {
    event?.preventDefault();
    this.closeOptions();
    await this.closeDialog();
    document.querySelector('#ax-product-focus')?.scrollIntoView({ behavior: 'smooth' });
  }
}

if (!customElements.get('story-player')) {
  customElements.define('story-player', StoryPlayer);
}
